import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';
import { HttpParams, HttpHeaders, HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, map, tap } from 'rxjs';
import { CognitoTokenResponse, CognitoUserInfoResponse, TimedOauthCredentials } from '@models/TimedOauthCredentials';

@Injectable({
  providedIn: 'root'
})
export class CognitoService {
  public accessToken$: BehaviorSubject<TimedOauthCredentials | undefined> = new BehaviorSubject<TimedOauthCredentials | undefined>(undefined);
  private _userInfo: CognitoUserInfoResponse | undefined;
  private _intervalId: NodeJS.Timeout | undefined;

  static TIMED_ACCESS_TOKEN = "TIMED_ACCESS_TOKEN";
  static REFRESH_TOKEN = "REFRESH_TOKEN";

  constructor(private http: HttpClient) { }

  /// this is only called from the synchronizer service
  public async initializeApplication(): Promise<CognitoUserInfoResponse> {
    if (!this.hasStoredCredentials) {
      console.info("No stored credentials");
      return Promise.reject();
    }

    try {
      const success = await this.ensureStoredAccessTokenIsFresh();
      if (success) {
        console.info(`Access token is fresh.`);
        this.storeAccessToken(this.accessToken$.value as TimedOauthCredentials);

        /// set a timer to refresh the token periodically
        this.setTokenRefreshTimer();

        return await this.getUserInfo();
      } else {
        console.error("Could not get a fresh access token.");
        this.clearStoredCredentials();
        return Promise.reject("Could not get a fresh access token.");
      }
    } catch (error) {
      console.error("Error ensuring stored access token is fresh", error);
      return Promise.reject("Could not get a fresh access token.");
    }
  }

  public get hasStoredCredentials(): boolean {
    return this.storedAccessToken !== undefined
      || this.refreshToken !== undefined;
  }

  private get storedAccessToken(): TimedOauthCredentials | undefined {
    const str = localStorage.getItem(CognitoService.TIMED_ACCESS_TOKEN);
    if (str === null) {
      return undefined;
    } else {
      return TimedOauthCredentials.fromString(str);
    }
  }

  private async setTokenRefreshTimer() {
    /// Interval time in milliseconds (25 minutes)
    /// (The docs say 30 minutes, but we'll be safe)
    const intervalTime = 25 * 60 * 1000;

    /// Clear any existing interval
    if (this._intervalId !== undefined) {
      clearInterval(this._intervalId);
    }

    // Set the interval to call the function every 25 minutes
    this._intervalId = setInterval(async () => {
      console.log("Refreshing token...");
      this.refreshAccessTokenFromRefreshToken(this.refreshToken || "");
    }, intervalTime);

    // this is the previous code; I haven't tested what's above
    // const outerThis = this;
    // this._intervalId = setInterval(function () {
    //   outerThis.refreshToken(outerThis.accessToken$.value?.refreshToken || "");
    // }, intervalTime);

  }

  private clearStoredCredentials() {
    localStorage.removeItem(CognitoService.TIMED_ACCESS_TOKEN);
    this.accessToken$.next(undefined);
  }

  public get loginUrl(): string {
    return `${environment.cognito.auth_url}/login?response_type=code&client_id=${environment.cognito.client_id}&redirect_uri=${encodeURIComponent(environment.cognito.redirect_uri)}`;
  }

  private get tokenUrl(): string {
    return `${environment.cognito.auth_url}/oauth2/token`;
  }

  private get userInfoUrl(): string {
    return `${environment.cognito.auth_url}/oauth2/userInfo`;
  }

  public async ensureStoredAccessTokenIsFresh(): Promise<boolean> {
    if (this.storedAccessToken) {
      /// something is stored, so we can use it
      this.accessToken$.next(this.storedAccessToken);
    } else {
      /// if nothing is stored, see if we can use the refresh token
      if (this.refreshToken !== undefined) {
        return await this.refreshAccessTokenFromRefreshToken(this.refreshToken);
      }
    }

    if (this.accessToken$.value?.isExpired) {
      if (this.refreshToken) {
        /// then try to refresh it:
        return await this.refreshAccessTokenFromRefreshToken(this.refreshToken);
      } else {
        console.error("No refresh token found, cannot refresh access token");
        return false;
      }
    } else {
      return true;
    }
  }

  /// https://docs.aws.amazon.com/cognito/latest/developerguide/token-endpoint.html
  public getToken(code: string): Promise<CognitoTokenResponse> {
    const authString = "Basic " + btoa(`${environment.cognito.client_id}:${environment.cognito.client_secret}`);

    const params = new HttpParams()
      .set('grant_type', 'authorization_code')
      .set('client_id', environment.cognito.client_id)
      .set('client_secret', environment.cognito.client_secret)
      .set('redirect_uri', environment.cognito.redirect_uri)
      .set('code', code);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      'Authorization': authString,
    });

    const observable = this.http.post<CognitoTokenResponse>(this.tokenUrl, params.toString(), { headers })
      .pipe(
        tap((tokenResponse: CognitoTokenResponse) => {
          if (tokenResponse && tokenResponse.access_token) {
            console.info("Access token:", tokenResponse.access_token);
            this.storeAccessToken(new TimedOauthCredentials(tokenResponse));
          } else {
            console.error("No access token returned", tokenResponse);
          }
        })
      );

    return firstValueFrom<CognitoTokenResponse>(observable);

  }

  /// https://docs.aws.amazon.com/cognito/latest/developerguide/userinfo-endpoint.html
  private getUserInfo(): Promise<CognitoUserInfoResponse> {
    if (this.accessToken$.value === undefined) {
      console.error("No access token found");
      return Promise.reject("No access token found");
    }

    const headers = new HttpHeaders({
      'Authorization': "Bearer " + this.accessToken$.value.accessToken,
    });

    const observable = this.http.get<CognitoUserInfoResponse>(this.userInfoUrl, { headers })
      .pipe(
        tap((data: CognitoUserInfoResponse) => {
          this._userInfo = data;
        })
      );
    return firstValueFrom(observable);
  }

  private refreshAccessTokenFromRefreshToken(refreshToken: string): Promise<boolean> {
    try {
      const authString = `Basic ${btoa(`${environment.cognito.client_id}:${environment.cognito.client_secret}`)}`;
      const params = new HttpParams()
        .set('grant_type', 'refresh_token')
        .set('client_id', environment.cognito.client_id)
        .set('refresh_token', refreshToken);

      const headers = new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': authString,
      });

      const observable = this.http.post<CognitoTokenResponse>(this.tokenUrl, params.toString(), { headers })
        .pipe(map((data: CognitoTokenResponse): boolean => {
          if (data && data.access_token) {
            console.info("Access token refreshed", data);
            this.storeAccessToken(new TimedOauthCredentials(data));
            return true;
          } else {
            console.error("Access token not refreshed", data);
            return false;
          }
        }));

      return firstValueFrom(observable);
    } catch (error) {
      console.error("Error refreshing token", error);
      return Promise.resolve(false);
    }

  }

  public storeAccessToken(token: TimedOauthCredentials) {
    this.accessToken$.next(token);
    localStorage.setItem(CognitoService.TIMED_ACCESS_TOKEN, token.toString());
    if (token.refreshToken) {
      console.info("Saving refresh token");
      localStorage.setItem(CognitoService.REFRESH_TOKEN, token.refreshToken);
    }
  }

  get refreshToken(): string | undefined {
    const str = localStorage.getItem(CognitoService.REFRESH_TOKEN);
    if (str === null) {
      return undefined;
    } else {
      return str;
    }
  }

}
